/**
 * @file MaterialChallenge.jsx
 * @description Redesigned challenge view with Material Design 3 and new navigation structure
 */

import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../../firebase";
import { logIn } from "../../UserAuth";
import { collection, doc, getDoc, getDocs, writeBatch, setDoc, deleteField } from "firebase/firestore";
import { MaterialHeader } from "../MaterialHeader";
import { useViewportHeight } from "../../utils/viewportUtils";
import { useTextSize } from "../../contexts/TextSizeContext";
import { Icon } from "../Icon";
import { ChallengeReadingTab, ChallengeLeaderboardTab, ChallengeInfoTab, ChallengeSettingsTab } from "./MaterialChallenge/";
import {
  getReadingPortionsFromBooks,
} from "../../utils/bible_utils";
import {
  getDatesToChapters,
  markReadingPortion,
  addUserToChallenge,
  addUserToChallengeByEmail,
  checkIfUserIsInChallenge,
} from "./challenges";
import { updateArchivedStatus, createUserInDatabaseIfDoesNotExist } from "../../firebase";
import { BIBLE_LANGUAGES } from "../../constants";
import { getBibleLanguageFromCookie, saveBibleLanguageToCookie } from "../../utils/theme_utils";
import {
  parseDateList,
  getTodayNormalized,
  normalizeToStartOfDay,
  isSameDay,
} from "../../utils/dateUtils";

const parseDateValue = (dateString) => {
  if (!dateString || typeof dateString !== "string") {
    return null;
  }
  const parsed = Date.parse(`${dateString}T00:00:00`);
  return Number.isNaN(parsed) ? null : parsed;
};

export const MaterialChallenge = () => {
  const location = useLocation();
  const history = useHistory();
  const authState = useAuthState(auth);
  const [user, loading] = authState || [null, false, null];
  const viewportHeight = useViewportHeight();
  
  // URL params
  const queryParams = new URLSearchParams(location.search);
  const challengeID = queryParams.get("id");
  const fromCreate = queryParams.get("from") === "create";
  const initialTab = queryParams.get("tab") || 'reading';
  
  // Email link params
  const emailUID = queryParams.get("uid");
  const markComplete = queryParams.get("markComplete");
  const dayToMarkComplete = markComplete ? parseInt(markComplete) - 1 : null; // Convert to 0-based index
  
  // Core data
  const [challengeData, setChallengeData] = useState(null);
  const [userChallengeData, setUserChallengeData] = useState(null);
  const [datesToChapters, setDatesToChapters] = useState([]);
  
  // UI state
  const [activeTab, setActiveTab] = useState(initialTab);
  const [currentDay, setCurrentDay] = useState(0);
  
  const [loadingChallenge, setLoadingChallenge] = useState(true);

  // Bible text caching
  const [bibleCache, setBibleCache] = useState(new Map());
  const [loadingDays, setLoadingDays] = useState(new Set());

  // Loading states for various operations
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isLeavingJoining, setIsLeavingJoining] = useState(false);
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [isUpdatingChallengeType, setIsUpdatingChallengeType] = useState(false);
  const [isUpdatingGoalPercent, setIsUpdatingGoalPercent] = useState(false);
  const [isSavingBookmark, setIsSavingBookmark] = useState(false);

  // Modal states
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [userIsInChallenge, setUserIsInChallenge] = useState(true);
  const [hasInitiallyNavigated, setHasInitiallyNavigated] = useState(false);
  const [challengeMembers, setChallengeMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isChallengeAdmin, setIsChallengeAdmin] = useState(false);
  const [hasAnyAdmin, setHasAnyAdmin] = useState(false);
  const [adminUpdateUserId, setAdminUpdateUserId] = useState(null);
  const [isRenamingChallenge, setIsRenamingChallenge] = useState(false);
  const [isClaimingAdmin, setIsClaimingAdmin] = useState(false);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const [removeMemberTarget, setRemoveMemberTarget] = useState(null);
  const [showAdminRoleModal, setShowAdminRoleModal] = useState(false);
  const [adminRoleTarget, setAdminRoleTarget] = useState(null);
  const [adminRoleNextValue, setAdminRoleNextValue] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isInvitingByEmail, setIsInvitingByEmail] = useState(false);
  const [showInviteFromChallengeModal, setShowInviteFromChallengeModal] = useState(false);
  const [inviteStep, setInviteStep] = useState("selectChallenge");
  const [inviteChallenges, setInviteChallenges] = useState([]);
  const [isLoadingInviteChallenges, setIsLoadingInviteChallenges] = useState(false);
  const [selectedInviteChallengeId, setSelectedInviteChallengeId] = useState(null);
  const [inviteChallengeMembers, setInviteChallengeMembers] = useState([]);
  const [selectedInviteMemberIds, setSelectedInviteMemberIds] = useState(new Set());
  const [isLoadingInviteMembers, setIsLoadingInviteMembers] = useState(false);
  const [isAddingInviteMembers, setIsAddingInviteMembers] = useState(false);
  
  // Toast state
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  
  // Text size from global context
  const { getVerseTextSizes } = useTextSize();
  
  // Email link processing state
  const [emailLinkProcessed, setEmailLinkProcessed] = useState(false);
  const [emailLinkProcessing, setEmailLinkProcessing] = useState(false);

  const showToastMessage = useCallback((message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  }, []);

  const checkUserChallengeStatus = useCallback(async () => {
    if (!user || !challengeID) return;
    try {
      const isInChallenge = await checkIfUserIsInChallenge(db, user.uid, challengeID);
      setUserIsInChallenge(isInChallenge);
    } catch (error) {
      console.error('Error checking user challenge status:', error);
    }
  }, [user, challengeID]);

  const fetchChallengeData = useCallback(async () => {
    if (!challengeID) return;
    try {
      const challengeDoc = await getDoc(doc(db, "challenges", challengeID));
      if (challengeDoc.exists()) {
        const data = challengeDoc.data();
        setChallengeData(data);
        
        const portions = getReadingPortionsFromBooks(data.books);
        const dateMapping = getDatesToChapters(
          data.start_date,
          data.end_date || "",
          data.days,
          portions,
          parseDateList(data.skip_dates || "")
        );
        setDatesToChapters(Array.isArray(dateMapping) ? dateMapping : []);
      }
      setLoadingChallenge(false);
    } catch (error) {
      console.error("Error fetching challenge:", error);
      setLoadingChallenge(false);
    }
  }, [challengeID]);

  const fetchUserChallengeData = useCallback(async () => {
    if (!user || !challengeID) return;
    try {
      const userDoc = await getDoc(doc(db, "users", user.uid, "challenges", challengeID));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Handle progress string length mismatch
        if (userData.progress && datesToChapters.length > 0) {
          const expectedLength = datesToChapters.length;
          let progress = userData.progress;
          
          if (progress.length < expectedLength) {
            // Fill with 0s if progress is shorter
            progress = progress.padEnd(expectedLength, '0');
          } else if (progress.length > expectedLength) {
            // Truncate if progress is longer
            progress = progress.substring(0, expectedLength);
          }
          
          userData.progress = progress;
        }
        
        setUserChallengeData(userData);
      }
    } catch (error) {
      console.error("Error fetching user challenge data:", error);
    }
  }, [user, challengeID, datesToChapters.length]);

  const fetchChallengeMembers = useCallback(async (retryCount = 0) => {
    if (!user || !challengeID || !userIsInChallenge) return;
    const maxRetries = 3;
    setIsLoadingMembers(true);

    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 7000)
      );
      const membersRef = collection(db, "challenges", challengeID, "users");
      const fetchPromise = getDocs(membersRef);
      const membersSnapshot = await Promise.race([fetchPromise, timeoutPromise]);

      const members = await Promise.all(
        membersSnapshot.docs.map(async (memberDoc) => {
          const memberId = memberDoc.id;
          const memberData = memberDoc.data();
          let displayName = "Unnamed User";
          let email = "";

          try {
            const userDoc = await getDoc(doc(db, "users", memberId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              displayName = userData.displayName || displayName;
              email = userData.email || "";
            }
          } catch (error) {
            console.error("Error loading member profile:", error);
          }

          return {
            id: memberId,
            role: memberData?.role || null,
            displayName,
            email
          };
        })
      );

      setChallengeMembers(members);
      const adminFound = members.some((member) => member.role === "admin");
      setHasAnyAdmin(adminFound);
      setIsChallengeAdmin(
        members.some((member) => member.id === user.uid && member.role === "admin")
      );
    } catch (error) {
      console.error("Error fetching challenge members:", error);
      if (retryCount < maxRetries) {
        setTimeout(() => {
          fetchChallengeMembers(retryCount + 1);
        }, 1000 * Math.pow(2, retryCount));
      } else {
        showToastMessage("Failed to load challenge members.");
      }
    } finally {
      setIsLoadingMembers(false);
    }
  }, [user, challengeID, userIsInChallenge, showToastMessage]);

  const fetchUserSettings = useCallback(async () => {
    if (!user) return;
    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const bibleLanguage = userData.bibleLanguage || BIBLE_LANGUAGES.ENGLISH;
        saveBibleLanguageToCookie(bibleLanguage);
      }
    } catch (error) {
      console.error("Error fetching user settings:", error);
    }
  }, [user]);

  // Bible text caching and prefetching
  const fetchBibleTextForDay = useCallback(async (dayIndex) => {
    if (!datesToChapters[dayIndex] || loadingDays.has(dayIndex)) {
      return null;
    }

    const cacheKey = `day-${dayIndex}`;
    if (bibleCache.has(cacheKey)) {
      return bibleCache.get(cacheKey);
    }

    setLoadingDays(prev => new Set([...prev, dayIndex]));

    try {
      // Get Bible language from cookie first (immediate access)
      let bibleLanguage = getBibleLanguageFromCookie();
      
      // If no cookie, set default and update Firebase
      if (!bibleLanguage) {
        bibleLanguage = BIBLE_LANGUAGES.ENGLISH;
        saveBibleLanguageToCookie(bibleLanguage);
        
        // Also update Firebase if user is available
        if (user) {
          try {
            await setDoc(doc(db, "users", user.uid), {
              bibleLanguage: bibleLanguage
            }, { merge: true });
          } catch (error) {
            console.error("Error setting default Bible language in Firebase:", error);
          }
        }
      }

      const { fetchReadings } = await import('../../utils/lsm_api');
      const readings = datesToChapters[dayIndex][1];
      
      const readingPairs = readings.map(reading => {
        if (typeof reading === 'string') {
          const parts = reading.replace(',', ' ').split(' ');
          if (parts.length >= 2) {
            const book = parts.slice(0, -1).join(' ');
            const chapter = parseInt(parts[parts.length - 1]);
            return [book, chapter];
          }
          return [reading, 1];
        } else if (Array.isArray(reading)) {
          return reading;
        }
        return [reading, 1];
      });

      const data = await fetchReadings(readingPairs, 3, 7000, bibleLanguage);
      
      // Cache the result
      setBibleCache(prev => new Map([...prev, [cacheKey, data]]));
      
      return data;
    } catch (error) {
      console.error(`Error fetching Bible text for day ${dayIndex}:`, error);
      
      // Show appropriate error message based on error type
      if (error.message && error.message.includes('timeout')) {
        showToastMessage('Error fetching LSM verses, please try again later.');
      } else if (error.message && error.message.includes('Failed to connect to emulators')) {
        showToastMessage('Failed to connect to emulators, did you run firebase emulators:start?');
      } else {
        showToastMessage('Failed to load Bible text. Please check your connection.');
      }
      
      return null;
    } finally {
      setLoadingDays(prev => {
        const newSet = new Set(prev);
        newSet.delete(dayIndex);
        return newSet;
      });
    }
  }, [datesToChapters, loadingDays, user, bibleCache, showToastMessage]);

  const prefetchBibleText = useCallback(async (currentDayIndex) => {
    // Prefetch current day, adjacent days, and first unread
    const daysToPrefetch = new Set([currentDayIndex]);
    
    // Add adjacent days
    if (currentDayIndex > 0) daysToPrefetch.add(currentDayIndex - 1);
    if (currentDayIndex < datesToChapters.length - 1) daysToPrefetch.add(currentDayIndex + 1);
    
    // Add first unread day
    if (userChallengeData && userChallengeData.progress) {
      const progress = userChallengeData.progress;
      for (let i = 0; i < progress.length && i < datesToChapters.length; i++) {
        if (progress[i] === '0') {
          daysToPrefetch.add(i);
          break;
        }
      }
    }

    // Prefetch all identified days
    Array.from(daysToPrefetch).forEach(dayIndex => {
      fetchBibleTextForDay(dayIndex);
    });
  }, [datesToChapters.length, userChallengeData, fetchBibleTextForDay]);

  const handleMarkComplete = async (dayIndex) => {
    // Safety checks: user must be in challenge, challenge not archived, and not already processing
    if (!userChallengeData || datesToChapters.length === 0 || isMarkingComplete || 
        !userIsInChallenge || userChallengeData?.archived) {
      return;
    }

    setIsMarkingComplete(true);
    
    try {
      const isCurrentlyRead = userChallengeData.progress[dayIndex] === '1';
      const newStatus = !isCurrentlyRead;
      
      // markReadingPortion expects 1-based index, dayIndex is 0-based
      await markReadingPortion(db, user.uid, challengeID, dayIndex + 1, newStatus);
      
      // Refresh user data
      await fetchUserChallengeData();
    } catch (error) {
      console.error('Error updating reading status:', error);
      alert('Failed to update reading status. Please try again.');
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const handleSaveBookmark = useCallback(async (bookmark) => {
    if (!user || !challengeID || !userIsInChallenge || userChallengeData?.archived) {
      return;
    }
    if (!bookmark || !bookmark.book || !bookmark.chapter || !bookmark.verse) {
      return;
    }
    setIsSavingBookmark(true);
    try {
      await setDoc(doc(db, "users", user.uid, "challenges", challengeID), {
        bookmark: {
          book: bookmark.book,
          chapter: bookmark.chapter,
          verse: bookmark.verse
        }
      }, { merge: true });
      setUserChallengeData(prev => ({
        ...(prev || {}),
        bookmark: {
          book: bookmark.book,
          chapter: bookmark.chapter,
          verse: bookmark.verse
        }
      }));
      showToastMessage('Bookmark saved.');
    } catch (error) {
      console.error('Error saving bookmark:', error);
      showToastMessage('Failed to save bookmark.');
    } finally {
      setIsSavingBookmark(false);
    }
  }, [user, challengeID, userIsInChallenge, userChallengeData?.archived, showToastMessage]);

  const handleRemoveBookmark = useCallback(async () => {
    if (!user || !challengeID || !userIsInChallenge || userChallengeData?.archived) {
      return;
    }
    setIsSavingBookmark(true);
    try {
      await setDoc(doc(db, "users", user.uid, "challenges", challengeID), {
        bookmark: deleteField()
      }, { merge: true });
      setUserChallengeData(prev => {
        if (!prev) {
          return prev;
        }
        const next = { ...prev };
        delete next.bookmark;
        return next;
      });
      showToastMessage('Bookmark removed.');
    } catch (error) {
      console.error('Error removing bookmark:', error);
      showToastMessage('Failed to remove bookmark.');
    } finally {
      setIsSavingBookmark(false);
    }
  }, [user, challengeID, userIsInChallenge, userChallengeData?.archived, showToastMessage]);

  const goToPreviousDay = () => {
    console.log('goToPreviousDay called', { currentDay, datesToChaptersLength: datesToChapters.length });
    if (currentDay > 0 && datesToChapters.length > 0) {
      const newDay = currentDay - 1;
      setCurrentDay(newDay);
      console.log('Moving to previous day:', newDay);
      prefetchBibleText(newDay);
    }
  };

  const goToNextDay = () => {
    console.log('goToNextDay called', { currentDay, datesToChaptersLength: datesToChapters.length });
    if (currentDay < datesToChapters.length - 1 && datesToChapters.length > 0) {
      const newDay = currentDay + 1;
      setCurrentDay(newDay);
      console.log('Moving to next day:', newDay);
      prefetchBibleText(newDay);
    }
  };

  const currentVerseTextSizes = getVerseTextSizes();

  const handleToggleEmailUpdates = async () => {
    if (!user || !challengeID || !userChallengeData || !userIsInChallenge || userChallengeData?.archived || isUpdatingEmail) {
      return;
    }

    const emailUpdatesEnabled = userChallengeData?.emailUpdates !== false;
    const nextValue = !emailUpdatesEnabled;
    setIsUpdatingEmail(true);

    try {
      await setDoc(
        doc(db, "users", user.uid, "challenges", challengeID),
        { emailUpdates: nextValue },
        { merge: true }
      );
      await fetchUserChallengeData();
      showToastMessage(nextValue ? 'Email updates enabled.' : 'Email updates disabled.');
    } catch (error) {
      console.error('Error updating email preferences:', error);
      showToastMessage('Failed to update email preferences. Please try again.');
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleUpdateChallengeType = async (nextType) => {
    if (!user || !challengeID || !isChallengeAdmin || !nextType || isUpdatingChallengeType) {
      return;
    }
    if (nextType !== "leaderboard" && nextType !== "cooperative") {
      return;
    }
    setIsUpdatingChallengeType(true);
    try {
      await setDoc(
        doc(db, "challenges", challengeID),
        {
          challenge_type: nextType,
          ...(nextType === "leaderboard" ? { goal_percent: deleteField() } : {}),
        },
        { merge: true }
      );
      await fetchChallengeData();
      showToastMessage("Challenge type updated.");
    } catch (error) {
      console.error("Error updating challenge type:", error);
      showToastMessage("Failed to update challenge type.");
    } finally {
      setIsUpdatingChallengeType(false);
    }
  };

  const handleUpdateGoalPercent = async (nextGoalPercent) => {
    if (!user || !challengeID || !isChallengeAdmin || isUpdatingGoalPercent) {
      return;
    }
    if (!Number.isFinite(nextGoalPercent) || nextGoalPercent < 0 || nextGoalPercent > 100) {
      showToastMessage("Goal must be between 0 and 100.");
      return;
    }
    setIsUpdatingGoalPercent(true);
    try {
      await setDoc(
        doc(db, "challenges", challengeID),
        { goal_percent: Math.round(nextGoalPercent) },
        { merge: true }
      );
      await fetchChallengeData();
      showToastMessage("Goal updated.");
    } catch (error) {
      console.error("Error updating goal:", error);
      showToastMessage("Failed to update goal.");
    } finally {
      setIsUpdatingGoalPercent(false);
    }
  };

  const resetInviteFlow = useCallback(() => {
    setInviteStep("selectChallenge");
    setSelectedInviteChallengeId(null);
    setInviteChallengeMembers([]);
    setSelectedInviteMemberIds(new Set());
  }, []);

  const loadInviteChallenges = useCallback(async (retryCount = 0) => {
    if (!user || !challengeID || !isChallengeAdmin || isLoadingInviteChallenges) {
      return;
    }
    const maxRetries = 3;
    setIsLoadingInviteChallenges(true);
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Timeout")), 7000)
      );
      const userChallengesRef = collection(db, "users", user.uid, "challenges");
      const fetchPromise = getDocs(userChallengesRef);
      const userChallengesSnapshot = await Promise.race([
        fetchPromise,
        timeoutPromise,
      ]);
      const challengePromises = userChallengesSnapshot.docs.map(
        async (userChallengeDoc) => {
          const challengeId = userChallengeDoc.id;
          if (challengeId === challengeID) {
            return null;
          }
          const challengeDoc = await getDoc(doc(db, "challenges", challengeId));
          if (!challengeDoc.exists()) {
            return null;
          }
          return {
            id: challengeId,
            ...challengeDoc.data(),
            ...userChallengeDoc.data(),
          };
        }
      );
      const challenges = (await Promise.all(challengePromises)).filter(Boolean);
      challenges.sort((a, b) => {
        const aEnd = parseDateValue(a.end_date) ?? -Infinity;
        const bEnd = parseDateValue(b.end_date) ?? -Infinity;
        if (aEnd !== bEnd) {
          return bEnd - aEnd;
        }
        const aStart = parseDateValue(a.start_date) ?? -Infinity;
        const bStart = parseDateValue(b.start_date) ?? -Infinity;
        if (aStart !== bStart) {
          return bStart - aStart;
        }
        return (a.challenge_name || "").localeCompare(b.challenge_name || "");
      });
      setInviteChallenges(challenges);
    } catch (error) {
      console.error("Error loading invite challenges:", error);
      if (retryCount < maxRetries) {
        setTimeout(() => {
          loadInviteChallenges(retryCount + 1);
        }, 1000 * Math.pow(2, retryCount));
        return;
      }
      showToastMessage("Failed to load challenges.");
    } finally {
      setIsLoadingInviteChallenges(false);
    }
  }, [user, challengeID, isChallengeAdmin, isLoadingInviteChallenges, showToastMessage]);

  const loadInviteChallengeMembers = useCallback(
    async (sourceChallengeId, retryCount = 0) => {
      if (!user || !sourceChallengeId || isLoadingInviteMembers) {
        return;
      }
      const maxRetries = 3;
      setIsLoadingInviteMembers(true);
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Timeout")), 7000)
        );
        const membersRef = collection(
          db,
          "challenges",
          sourceChallengeId,
          "users"
        );
        const fetchPromise = getDocs(membersRef);
        const membersSnapshot = await Promise.race([
          fetchPromise,
          timeoutPromise,
        ]);
        const members = await Promise.all(
          membersSnapshot.docs.map(async (memberDoc) => {
            const memberId = memberDoc.id;
            let displayName = "Unnamed User";
            let email = "";
            let emailUpdates = true;
            try {
              const [userDoc, userChallengeDoc] = await Promise.all([
                getDoc(doc(db, "users", memberId)),
                getDoc(
                  doc(db, "users", memberId, "challenges", sourceChallengeId)
                ),
              ]);
              if (userDoc.exists()) {
                const userData = userDoc.data();
                displayName = userData.displayName || displayName;
                email = userData.email || "";
              }
              if (userChallengeDoc.exists()) {
                const userChallengeData = userChallengeDoc.data();
                emailUpdates = userChallengeData?.emailUpdates !== false;
              }
            } catch (error) {
              console.error("Error loading invite member profile:", error);
            }
            return {
              id: memberId,
              displayName,
              email,
              emailUpdates,
            };
          })
        );
        setInviteChallengeMembers(members);
        setSelectedInviteMemberIds(new Set(members.map((member) => member.id)));
      } catch (error) {
        console.error("Error loading invite members:", error);
        if (retryCount < maxRetries) {
          setTimeout(() => {
            loadInviteChallengeMembers(sourceChallengeId, retryCount + 1);
          }, 1000 * Math.pow(2, retryCount));
          return;
        }
        showToastMessage("Failed to load challenge members.");
      } finally {
        setIsLoadingInviteMembers(false);
      }
    },
    [user, isLoadingInviteMembers, showToastMessage]
  );

  const handleInviteByEmail = async () => {
    if (!user || !challengeID || !isChallengeAdmin || isInvitingByEmail) {
      return;
    }
    const trimmedEmail = inviteEmail.trim();
    if (!trimmedEmail) {
      showToastMessage("Enter an email address.");
      return;
    }
    setIsInvitingByEmail(true);
    try {
      const [success, message] = await addUserToChallengeByEmail(
        db,
        challengeID,
        trimmedEmail
      );
      if (success) {
        setInviteEmail("");
        await fetchChallengeMembers();
      }
      showToastMessage(message);
    } catch (error) {
      console.error("Error inviting by email:", error);
      showToastMessage("Failed to add user by email.");
    } finally {
      setIsInvitingByEmail(false);
    }
  };

  const handleOpenInviteFromChallenge = () => {
    if (!isChallengeAdmin) {
      return;
    }
    resetInviteFlow();
    setShowInviteFromChallengeModal(true);
    if (inviteChallenges.length === 0 && !isLoadingInviteChallenges) {
      loadInviteChallenges();
    }
  };

  const handleCloseInviteFromChallenge = () => {
    if (isAddingInviteMembers) {
      return;
    }
    setShowInviteFromChallengeModal(false);
    resetInviteFlow();
  };

  const handleSelectInviteChallenge = (nextChallengeId) => {
    if (!nextChallengeId || nextChallengeId === selectedInviteChallengeId) {
      return;
    }
    setSelectedInviteChallengeId(nextChallengeId);
    setInviteStep("selectUsers");
    loadInviteChallengeMembers(nextChallengeId);
  };

  const handleToggleInviteMember = (memberId) => {
    setSelectedInviteMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  };

  const handleSelectAllInviteMembers = (selectAll) => {
    if (selectAll) {
      setSelectedInviteMemberIds(
        new Set(inviteChallengeMembers.map((member) => member.id))
      );
      return;
    }
    setSelectedInviteMemberIds(new Set());
  };

  const handleConfirmInviteMembers = async () => {
    if (
      !user ||
      !challengeID ||
      !selectedInviteChallengeId ||
      isAddingInviteMembers
    ) {
      return;
    }
    if (selectedInviteMemberIds.size === 0) {
      showToastMessage("Select at least one member.");
      return;
    }
    setIsAddingInviteMembers(true);
    try {
      const existingMemberIds = new Set(
        challengeMembers.map((member) => member.id)
      );
      const sourceMemberMap = new Map(
        inviteChallengeMembers.map((member) => [member.id, member])
      );
      const targetProgressLength =
        Number.isFinite(challengeData?.reading_portions) &&
        challengeData.reading_portions > 0
          ? challengeData.reading_portions
          : datesToChapters.length || 100;
      const batch = writeBatch(db);
      let addedCount = 0;
      let skippedCount = 0;
      selectedInviteMemberIds.forEach((memberId) => {
        if (existingMemberIds.has(memberId)) {
          skippedCount += 1;
          return;
        }
        const sourceMember = sourceMemberMap.get(memberId);
        const emailUpdates = sourceMember
          ? sourceMember.emailUpdates !== false
          : true;
        const userChallengeRef = doc(
          db,
          "users",
          memberId,
          "challenges",
          challengeID
        );
        const challengeUserRef = doc(
          db,
          "challenges",
          challengeID,
          "users",
          memberId
        );
        batch.set(userChallengeRef, {
          progress: "0".repeat(targetProgressLength),
          emailUpdates,
        });
        batch.set(challengeUserRef, {});
        addedCount += 1;
      });
      if (addedCount === 0) {
        showToastMessage("All selected users are already in this challenge.");
        return;
      }
      await batch.commit();
      await fetchChallengeMembers();
      showToastMessage(
        skippedCount > 0
          ? `Added ${addedCount} users. Skipped ${skippedCount} already in the challenge.`
          : `Added ${addedCount} users to the challenge.`
      );
      resetInviteFlow();
      setShowInviteFromChallengeModal(false);
    } catch (error) {
      console.error("Error adding members from challenge:", error);
      showToastMessage("Failed to add users from challenge.");
    } finally {
      setIsAddingInviteMembers(false);
    }
  };

  // Cookie utilities for temporary user handling
  const setTemporaryUserCookie = (uid) => {
    document.cookie = `temporaryUserId=${uid}; path=/; max-age=${30 * 24 * 60 * 60}`; // 30 days
  };

  const getTemporaryUserCookie = () => {
    const value = document.cookie
      .split('; ')
      .find(row => row.startsWith('temporaryUserId='))
      ?.split('=')[1];
    return value || null;
  };

  const clearTemporaryUserCookie = () => {
    document.cookie = 'temporaryUserId=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  };

  // Email link processing function
  const processEmailLink = useCallback(async () => {
    if (!emailUID || dayToMarkComplete === null || emailLinkProcessed || emailLinkProcessing) {
      return;
    }

    setEmailLinkProcessing(true);
    
    try {
      // Case 1: User is signed in
      if (user) {
        // Case 1a: User ID matches - mark reading complete
        if (user.uid === emailUID) {
          if (userIsInChallenge) {
            try {
              await markReadingPortion(db, user.uid, challengeID, dayToMarkComplete + 1, true);
              await fetchUserChallengeData();
              setCurrentDay(dayToMarkComplete);
              showToastMessage('Reading marked complete!');
            } catch (error) {
              console.error('Error marking reading complete:', error);
              showToastMessage('Failed to mark reading complete. Please try again.');
            }
          } else {
            showToastMessage('You are not in this challenge, so the reading cannot be marked complete.');
          }
        } 
        // Case 1b: User ID doesn't match - ignore email params and navigate normally
        else {
          showToastMessage('Email link was for a different user. Navigating to challenge normally.');
          // Clear URL params to prevent confusion
          history.replace(`/challenge?id=${challengeID}`)
        }
      } 
      // Case 2: User is not signed in
      else {
        // Check if the email UID exists in the database
        const userDoc = await getDoc(doc(db, "users", emailUID));
        
        if (userDoc.exists()) {
          // Case 2a: Email UID exists in database
          const isInChallenge = await checkIfUserIsInChallenge(db, emailUID, challengeID);
          
          // Add user to challenge if not already in it
          if (!isInChallenge) {
            await addUserToChallenge(db, emailUID, challengeID);
          }
          
          // Mark reading complete for the email UID
          await markReadingPortion(db, emailUID, challengeID, dayToMarkComplete + 1, true);
          
          // Set temporary user cookie for later migration
          setTemporaryUserCookie(emailUID);
          
          setCurrentDay(dayToMarkComplete);
          showToastMessage(
            'Reading marked complete! Please sign in to save your progress permanently. ' +
            '<a href="/settings" style="color: var(--md-sys-color-primary); text-decoration: underline;">Sign In</a>'
          );
        } else {
          // Case 2b: Email UID doesn't exist in database
          showToastMessage(
            'This email link is from an old email. Please sign in to your account to log your progress. ' +
            '<a href="/settings" style="color: var(--md-sys-color-primary); text-decoration: underline;">Sign In</a>'
          );
        }
      }
    } catch (error) {
      console.error('Error processing email link:', error);
      showToastMessage('Error processing email link. Please try again.');
    } finally {
      setEmailLinkProcessing(false);
      setEmailLinkProcessed(true);
    }
  }, [emailUID, dayToMarkComplete, emailLinkProcessed, emailLinkProcessing, user, userIsInChallenge, challengeID, history, fetchUserChallengeData, showToastMessage]);

  // User migration function for when temporary user signs in
  const migrateTemporaryUserData = useCallback(async (permanentUser) => {
    const temporaryUID = getTemporaryUserCookie();
    if (!temporaryUID || temporaryUID === permanentUser.uid) {
      return;
    }

    try {
      // First, get the temporary user document to access their email time
      const tempUserDoc = await getDoc(doc(db, "users", temporaryUID));
      if (!tempUserDoc.exists()) {
        // Temporary user doesn't exist, clear cookie and exit
        clearTemporaryUserCookie();
        return;
      }
      
      const tempUserData = tempUserDoc.data();
      const tempUserEmailTime = tempUserData.gmtEmailTime;

      // Get all challenges from temporary user
      const tempChallengesRef = collection(db, "users", temporaryUID, "challenges");
      const tempChallengesSnap = await getDocs(tempChallengesRef);
      
      if (!tempChallengesSnap.empty) {
        const batch = writeBatch(db);
        
        for (const challengeDoc of tempChallengesSnap.docs) {
          const challengeData = challengeDoc.data();
          const challengeId = challengeDoc.id;
          const permanentUserChallengeRef = doc(db, "users", permanentUser.uid, "challenges", challengeId);
          
          batch.set(permanentUserChallengeRef, challengeData, { merge: true });
          
          const tempChallengeUserRef = doc(db, "challenges", challengeId, "users", temporaryUID);
          const permChallengeUserRef = doc(db, "challenges", challengeId, "users", permanentUser.uid);
          const tempChallengeUserDoc = await getDoc(tempChallengeUserRef);
          const tempRole = tempChallengeUserDoc.exists() ? tempChallengeUserDoc.data()?.role : null;
          
          batch.delete(tempChallengeUserRef);
          if (tempRole) {
            batch.set(permChallengeUserRef, { role: tempRole }, { merge: true });
          } else {
            batch.set(permChallengeUserRef, {});
          }
        }
        
        // Remove temporary user from email_times document
        if (tempUserEmailTime) {
          const tempEmailTimeUserRef = doc(db, "email_times", tempUserEmailTime, "users", temporaryUID);
          batch.delete(tempEmailTimeUserRef);
        }
        
        // Delete temporary user document
        const tempUserRef = doc(db, "users", temporaryUID);
        batch.delete(tempUserRef);
        
        await batch.commit();
        
        // Clear temporary user cookie
        clearTemporaryUserCookie();
        
        showToastMessage('Progress migrated successfully from temporary account!');
        
        // Refresh user challenge data
        await fetchUserChallengeData();
      } else {
        // No challenges to migrate, but still need to clean up temporary user and email_times
        const batch = writeBatch(db);
        
        // Remove temporary user from email_times document
        if (tempUserEmailTime) {
          const tempEmailTimeUserRef = doc(db, "email_times", tempUserEmailTime, "users", temporaryUID);
          batch.delete(tempEmailTimeUserRef);
        }
        
        // Delete temporary user document
        const tempUserRef = doc(db, "users", temporaryUID);
        batch.delete(tempUserRef);
        
        await batch.commit();
        
        // Clear temporary user cookie
        clearTemporaryUserCookie();
      }
    } catch (error) {
      console.error('Error migrating temporary user data:', error);
      showToastMessage('Error migrating account data. Please contact support.');
    }
  }, [fetchUserChallengeData, showToastMessage]);

  useEffect(() => {
    if (user && userIsInChallenge) {
      fetchChallengeMembers();
    } else {
      setChallengeMembers([]);
      setIsChallengeAdmin(false);
      setHasAnyAdmin(false);
    }
  }, [user, userIsInChallenge, fetchChallengeMembers]);

  const handleRenameChallenge = async (nextName) => {
    if (!user || !challengeID || !isChallengeAdmin || !nextName || isRenamingChallenge) {
      return;
    }

    const trimmedName = nextName.trim();
    if (!trimmedName) {
      return;
    }

    setIsRenamingChallenge(true);
    try {
      await setDoc(
        doc(db, "challenges", challengeID),
        { challenge_name: trimmedName },
        { merge: true }
      );
      await fetchChallengeData();
      showToastMessage("Challenge renamed.");
    } catch (error) {
      console.error("Error renaming challenge:", error);
      showToastMessage("Failed to rename challenge.");
    } finally {
      setIsRenamingChallenge(false);
    }
  };

  const handleToggleAdminRole = async (memberId, makeAdmin) => {
    if (!user || !challengeID || !isChallengeAdmin || !memberId || adminUpdateUserId) {
      return;
    }

    setAdminUpdateUserId(memberId);
    try {
      await setDoc(
        doc(db, "challenges", challengeID, "users", memberId),
        makeAdmin ? { role: "admin" } : { role: deleteField() },
        { merge: true }
      );
      await fetchChallengeMembers();
      showToastMessage(makeAdmin ? "Admin added." : "Admin removed.");
    } catch (error) {
      console.error("Error updating admin role:", error);
      showToastMessage("Failed to update admin role.");
    } finally {
      setAdminUpdateUserId(null);
    }
  };

  const handleClaimAdmin = async () => {
    if (!user || !challengeID || !userIsInChallenge || hasAnyAdmin || challengeData?.created_by || isClaimingAdmin) {
      return;
    }

    setIsClaimingAdmin(true);
    try {
      const batch = writeBatch(db);
      batch.set(
        doc(db, "challenges", challengeID),
        { created_by: user.uid },
        { merge: true }
      );
      batch.set(
        doc(db, "challenges", challengeID, "users", user.uid),
        { role: "admin" },
        { merge: true }
      );
      await batch.commit();
      await fetchChallengeData();
      await fetchChallengeMembers();
      showToastMessage("You are now an admin.");
    } catch (error) {
      console.error("Error claiming admin role:", error);
      showToastMessage("Failed to claim admin role.");
    } finally {
      setIsClaimingAdmin(false);
    }
  };

  const handleRemoveMemberClick = (member) => {
    if (!member || !member.id || member.id === user?.uid || !isChallengeAdmin) {
      return;
    }
    setRemoveMemberTarget(member);
    setShowRemoveMemberModal(true);
  };

  const handleAdminRoleClick = (member, makeAdmin) => {
    if (!member || !member.id || !isChallengeAdmin || adminUpdateUserId) {
      return;
    }
    setAdminRoleTarget(member);
    setAdminRoleNextValue(makeAdmin);
    setShowAdminRoleModal(true);
  };

  const confirmAdminRoleChange = async () => {
    if (!adminRoleTarget || adminRoleNextValue == null) {
      return;
    }
    await handleToggleAdminRole(adminRoleTarget.id, adminRoleNextValue);
    setShowAdminRoleModal(false);
    setAdminRoleTarget(null);
    setAdminRoleNextValue(null);
  };

  const confirmRemoveMember = async () => {
    if (!removeMemberTarget || !removeMemberTarget.id || !challengeID || !isChallengeAdmin || isRemovingMember) {
      return;
    }

    setIsRemovingMember(true);
    try {
      const batch = writeBatch(db);
      batch.delete(doc(db, "challenges", challengeID, "users", removeMemberTarget.id));
      batch.delete(doc(db, "users", removeMemberTarget.id, "challenges", challengeID));
      await batch.commit();
      await fetchChallengeMembers();
      showToastMessage("Member removed from challenge.");
    } catch (error) {
      console.error("Error removing member:", error);
      showToastMessage("Failed to remove member.");
    } finally {
      setIsRemovingMember(false);
      setShowRemoveMemberModal(false);
      setRemoveMemberTarget(null);
    }
  };

  const goToFirstUnread = () => {
    console.log('goToFirstUnread called', { 
      hasUserData: !!userChallengeData, 
      progress: userChallengeData?.progress,
      datesToChaptersLength: datesToChapters.length 
    });
    if (userChallengeData && userChallengeData.progress && datesToChapters.length > 0) {
      // Find first '0' in progress string (0-based index)
      const progress = userChallengeData.progress;
      for (let i = 0; i < progress.length && i < datesToChapters.length; i++) {
        if (progress[i] === '0') {
          console.log('Found first unread at index:', i);
          setCurrentDay(i);
          prefetchBibleText(i);
          return;
        }
      }
      console.log('No unread portions found');
      showToastMessage('All readings are complete!');
    }
  };

  const goToSpecificDay = (dayIndex) => {
    if (datesToChapters.length === 0 || dayIndex < 0 || dayIndex >= datesToChapters.length) {
      return;
    }
    
    // Check if we're already on the target day
    if (currentDay === dayIndex) {
      const dateStr = new Date(datesToChapters[dayIndex][0]).toLocaleDateString();
      showToastMessage(`You're already viewing ${dateStr}`);
      return;
    }
    
    // Navigate to reading tab and set the specific day
    setActiveTab('reading');
    setCurrentDay(dayIndex);
    prefetchBibleText(dayIndex);
    
    const dateStr = new Date(datesToChapters[dayIndex][0]).toLocaleDateString();
    showToastMessage(`Navigated to ${dateStr}`);
  };

  const goToToday = () => {
    if (datesToChapters.length === 0) {
      return;
    }
    
    // Get today's date using centralized date utilities
    const today = getTodayNormalized();
    
    let closestDayIndex = 0;
    let minDifference = Infinity;
    let exactMatch = false;

    for (let index = 0; index < datesToChapters.length; index++) {
      const dayData = datesToChapters[index];
      
      // Normalize challenge date using centralized utilities
      const challengeDate = dayData[0];
      const normalizedChallengeDate = normalizeToStartOfDay(challengeDate);
      
      const difference = Math.abs(today.getTime() - normalizedChallengeDate.getTime());
      
      // Check if it's the same date using centralized comparison
      if (isSameDay(today, normalizedChallengeDate)) {
        exactMatch = true;
        closestDayIndex = index;
        minDifference = 0;
        break; // Exit early when exact match is found
      }
      
      if (difference < minDifference) {
        minDifference = difference;
        closestDayIndex = index;
      }
    }

    // Check if we're already on the target day
    if (currentDay === closestDayIndex) {
      if (exactMatch) {
        showToastMessage("You're already viewing today's reading! 📅");
      } else {
        const closestDate = new Date(datesToChapters[closestDayIndex][0]);
        const dateStr = closestDate.toLocaleDateString();
        showToastMessage(`You're already on the closest date: ${dateStr}`);
      }
      return;
    }
    
    setCurrentDay(closestDayIndex);
    prefetchBibleText(closestDayIndex);
    
    if (!exactMatch) {
      const closestDate = new Date(datesToChapters[closestDayIndex][0]);
      const dateStr = closestDate.toLocaleDateString();
      showToastMessage(`Navigated to closest date: ${dateStr}`);
    }
  };

  // useEffect hooks - placed after all function definitions
  useEffect(() => {
    if (challengeID) {
      fetchChallengeData();
    }
  }, [challengeID, fetchChallengeData]);


  useEffect(() => {
    if (user && challengeID) {
      fetchUserChallengeData();
      fetchUserSettings();
    }
  }, [user, challengeID, datesToChapters.length, fetchUserChallengeData, fetchUserSettings]); // Re-fetch when datesToChapters changes

  useEffect(() => {
    if (datesToChapters.length > 0 && userChallengeData && userChallengeData.progress && !hasInitiallyNavigated) {
      // Find the day closest to today's date using centralized date utilities
      const today = getTodayNormalized();
      let closestDayIndex = 0;
      let minDifference = Infinity;

      for (let index = 0; index < datesToChapters.length; index++) {
        const dayData = datesToChapters[index];
        
        // Normalize challenge date using centralized utilities
        const challengeDate = dayData[0];
        const normalizedChallengeDate = normalizeToStartOfDay(challengeDate);
        
        const difference = Math.abs(today.getTime() - normalizedChallengeDate.getTime());
        
        // Check if it's the same date using centralized comparison
        if (isSameDay(today, normalizedChallengeDate)) {
          closestDayIndex = index;
          minDifference = 0;
          break; // Exit early when exact match is found
        }
        
        if (difference < minDifference) {
          minDifference = difference;
          closestDayIndex = index;
        }
      }

      setCurrentDay(closestDayIndex);
      setHasInitiallyNavigated(true);
      
      // Start prefetching after setting current day
      prefetchBibleText(closestDayIndex);
    }
  }, [datesToChapters, userChallengeData, hasInitiallyNavigated, prefetchBibleText]);

  useEffect(() => {
    if (user && challengeID) {
      checkUserChallengeStatus();
    }
  }, [user, challengeID, checkUserChallengeStatus]);

  // Email link processing effect
  useEffect(() => {
    if (challengeData && datesToChapters.length > 0 && !loading) {
      processEmailLink();
    }
  }, [challengeData, datesToChapters, user, loading, emailUID, dayToMarkComplete, userIsInChallenge, processEmailLink]);

  // User migration effect (when user signs in and there's a temporary user cookie)
  useEffect(() => {
    if (user && !loading) {
      migrateTemporaryUserData(user);
    }
  }, [user, loading, migrateTemporaryUserData]);

  useEffect(() => {
    return () => {
      sessionStorage.setItem('refreshChallenges', 'true');
    };
  }, []);

  if (loading || loadingChallenge) {
    return (
      <div style={{ height: viewportHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <md-circular-progress indeterminate></md-circular-progress>
      </div>
    );
  }

  if (!challengeData) {
    return (
      <div style={{ height: viewportHeight, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <Icon name="error" size="hero" style={{ color: 'var(--md-sys-color-error)', marginBottom: '16px' }} />
        <h1 style={{ color: 'var(--md-sys-color-on-surface)', marginBottom: '16px' }}>Challenge Not Found</h1>
        <md-filled-button onClick={() => history.push('/')}>
          <Icon name="home" slot="icon" />
          Go to Home
        </md-filled-button>
      </div>
    );
  }

  const challengeTitle = challengeData.challenge_name || 'Untitled Challenge';
  const truncatedTitle = challengeTitle.length > 30 ? challengeTitle.substring(0, 27) + '...' : challengeTitle;

  const handleArchiveChallenge = () => {
    setShowArchiveModal(true);
  };

  const confirmArchiveChallenge = async () => {
    const isCurrentlyArchived = userChallengeData?.archived;
    const action = isCurrentlyArchived ? 'unarchive' : 'archive';
    
    setIsArchiving(true);
    try {
      await updateArchivedStatus(db, user.uid, challengeID, !isCurrentlyArchived);
      await fetchUserChallengeData();
      setToastMessage(`Challenge ${action}d successfully!`);
    } catch (error) {
      console.error(`Error ${action}ing challenge:`, error);
      setToastMessage(`Failed to ${action} challenge`);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleLeaveJoinChallenge = () => {
    if (userIsInChallenge) {
      // Show leave confirmation modal
      setShowLeaveModal(true);
    } else {
      // Join challenge directly (no confirmation needed)
      joinChallenge();
    }
  };

  const confirmLeaveChallenge = async () => {
    setIsLeavingJoining(true);
    try {
      const batch = writeBatch(db);
      
      // Remove user from challenge participants
      const challengeUserRef = doc(db, "challenges", challengeID, "users", user.uid);
      batch.delete(challengeUserRef);
      
      // Remove challenge from user's challenges
      const userChallengeRef = doc(db, "users", user.uid, "challenges", challengeID);
      batch.delete(userChallengeRef);
      
      await batch.commit();
      
      setUserIsInChallenge(false);
      setToastMessage('Successfully left the challenge');
    } catch (error) {
      console.error('Error leaving challenge:', error);
      setToastMessage('Failed to leave challenge');
    } finally {
      setIsLeavingJoining(false);
    }
  };

  const joinChallenge = async () => {
    setIsLeavingJoining(true);
    try {
      // Ensure user exists in database
      await createUserInDatabaseIfDoesNotExist(db, user);
      
      const batch = writeBatch(db);
      
      // Add user to challenge participants
      const challengeUserRef = doc(db, "challenges", challengeID, "users", user.uid);
      batch.set(challengeUserRef, {});
      
      // Add challenge to user's challenges
      const userChallengeRef = doc(db, "users", user.uid, "challenges", challengeID);
      batch.set(userChallengeRef, {
        progress: "0".repeat(datesToChapters.length || 100),
        emailUpdates: true,
      });
      
      await batch.commit();
      
      setUserIsInChallenge(true);
      await fetchUserChallengeData();
      setToastMessage('Successfully joined the challenge!');
    } catch (error) {
      console.error('Error joining challenge:', error);
      setToastMessage('Failed to join challenge');
    } finally {
      setIsLeavingJoining(false);
    }
  };

  const handleBackNavigation = () => {
    // Replace the current history entry to clean up the URL and remove the 'from' parameter
    history.replace(`/challenge?id=${challengeID}`);
    if (fromCreate) {
      history.push('/');
      return;
    } else if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push('/');
    }
  };

  const challengeType = challengeData?.challenge_type || "leaderboard";
  const goalPercent = Number.isFinite(challengeData?.goal_percent)
    ? Math.round(challengeData.goal_percent)
    : null;

  return (
    <div style={{ height: viewportHeight, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header with back button and title */}
      <MaterialHeader
        variant="back"
        title={truncatedTitle}
        onBack={handleBackNavigation}
      />
      
      {/* Warning Banner */}
      {(userChallengeData?.archived || !userIsInChallenge || !user) && (
        <div 
          onClick={() => {
            if (!user) {
              logIn();
            } else if (!userIsInChallenge && !userChallengeData?.archived) {
              setActiveTab('settings');
            }
          }}
          style={{
            backgroundColor: 'var(--md-sys-color-error-container)',
            color: 'var(--md-sys-color-on-error-container)',
            padding: '12px 16px',
            borderBottom: '1px solid var(--md-sys-color-outline-variant)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: (!user || (!userIsInChallenge && !userChallengeData?.archived)) ? 'pointer' : 'default',
            transition: 'opacity 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (!user || (!userIsInChallenge && !userChallengeData?.archived)) {
              e.currentTarget.style.opacity = '0.9';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          <Icon name="warning" style={{ fontSize: '20px' }} />
          {!user 
            ? "Sign in to join this challenge and log readings."
            : userChallengeData?.archived 
              ? "This challenge is archived." 
              : "Tap to join this challenge and log readings."
          }
          {(!user || (!userIsInChallenge && !userChallengeData?.archived)) && (
            <Icon name="next" style={{ fontSize: '18px', marginLeft: 'auto' }} />
          )}
        </div>
      )}

      {/* Main content area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Tab content */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {activeTab === 'reading' && (
            <ChallengeReadingTab 
              currentDay={currentDay}
              datesToChapters={datesToChapters}
              userChallengeData={userChallengeData}
              onPrevious={goToPreviousDay}
              onNext={goToNextDay}
              onGoToFirstUnread={goToFirstUnread}
              onGoToToday={goToToday}
              onMarkComplete={handleMarkComplete}
              bibleCache={bibleCache}
              fetchBibleTextForDay={fetchBibleTextForDay}
              loadingDays={loadingDays}
              isMarkingComplete={isMarkingComplete}
              userIsInChallenge={userIsInChallenge}
              verseTextSizes={currentVerseTextSizes}
              user={user}
              onSignIn={logIn}
              bookmark={userChallengeData?.bookmark}
              onSaveBookmark={handleSaveBookmark}
              onRemoveBookmark={handleRemoveBookmark}
              isSavingBookmark={isSavingBookmark}
              showToastMessage={showToastMessage}
            />
          )}
          {activeTab === 'leaderboard' && (
            <ChallengeLeaderboardTab 
              challengeID={challengeID} 
              challengeData={challengeData}
              datesToChapters={datesToChapters}
              user={user}
              challengeType={challengeType}
              goalPercent={goalPercent}
              isChallengeAdmin={isChallengeAdmin}
            />
          )}
          {activeTab === 'info' && (
            <ChallengeInfoTab 
              challengeData={challengeData} 
              datesToChapters={datesToChapters} 
              userChallengeData={userChallengeData}
              onDateClick={goToSpecificDay}
            />
          )}
          {activeTab === 'settings' && (
            <ChallengeSettingsTab 
              challengeID={challengeID} 
              challengeData={challengeData}
              userChallengeData={userChallengeData}
              user={user}
              challengeMembers={challengeMembers}
              isLoadingMembers={isLoadingMembers}
              isChallengeAdmin={isChallengeAdmin}
              hasAnyAdmin={hasAnyAdmin}
              adminUpdateUserId={adminUpdateUserId}
              isRenamingChallenge={isRenamingChallenge}
              isClaimingAdmin={isClaimingAdmin}
              isRemovingMember={isRemovingMember}
              showRemoveMemberModal={showRemoveMemberModal}
              removeMemberTarget={removeMemberTarget}
              showAdminRoleModal={showAdminRoleModal}
              adminRoleTarget={adminRoleTarget}
              adminRoleNextValue={adminRoleNextValue}
              fetchUserChallengeData={fetchUserChallengeData}
              onRenameChallenge={handleRenameChallenge}
              onToggleAdminRole={handleToggleAdminRole}
              onClaimAdmin={handleClaimAdmin}
              onRemoveMember={handleRemoveMemberClick}
              onAdminRoleClick={handleAdminRoleClick}
              onConfirmAdminRoleChange={confirmAdminRoleChange}
              onCloseAdminRoleModal={() => {
                if (adminUpdateUserId) return;
                setShowAdminRoleModal(false);
                setAdminRoleTarget(null);
                setAdminRoleNextValue(null);
              }}
              onConfirmRemoveMember={confirmRemoveMember}
              onCloseRemoveMemberModal={() => {
                if (isRemovingMember) return;
                setShowRemoveMemberModal(false);
                setRemoveMemberTarget(null);
              }}
              isArchiving={isArchiving}
              setIsArchiving={setIsArchiving}
              isLeavingJoining={isLeavingJoining}
              setIsLeavingJoining={setIsLeavingJoining}
              userIsInChallenge={userIsInChallenge}
              setUserIsInChallenge={setUserIsInChallenge}
              datesToChapters={datesToChapters}
              handleArchiveChallenge={handleArchiveChallenge}
              handleLeaveJoinChallenge={handleLeaveJoinChallenge}
              showArchiveModal={showArchiveModal}
              setShowArchiveModal={setShowArchiveModal}
              showLeaveModal={showLeaveModal}
              setShowLeaveModal={setShowLeaveModal}
              confirmArchiveChallenge={confirmArchiveChallenge}
              confirmLeaveChallenge={confirmLeaveChallenge}
              onToggleEmailUpdates={handleToggleEmailUpdates}
              isUpdatingEmail={isUpdatingEmail}
              challengeType={challengeType}
              goalPercent={goalPercent}
              onUpdateChallengeType={handleUpdateChallengeType}
              onUpdateGoalPercent={handleUpdateGoalPercent}
              isUpdatingChallengeType={isUpdatingChallengeType}
              isUpdatingGoalPercent={isUpdatingGoalPercent}
              inviteEmail={inviteEmail}
              setInviteEmail={setInviteEmail}
              isInvitingByEmail={isInvitingByEmail}
              onInviteByEmail={handleInviteByEmail}
              onOpenInviteFromChallenge={handleOpenInviteFromChallenge}
              showInviteFromChallengeModal={showInviteFromChallengeModal}
              inviteStep={inviteStep}
              setInviteStep={setInviteStep}
              inviteChallenges={inviteChallenges}
              isLoadingInviteChallenges={isLoadingInviteChallenges}
              selectedInviteChallengeId={selectedInviteChallengeId}
              onSelectInviteChallenge={handleSelectInviteChallenge}
              inviteChallengeMembers={inviteChallengeMembers}
              selectedInviteMemberIds={selectedInviteMemberIds}
              onToggleInviteMember={handleToggleInviteMember}
              onSelectAllInviteMembers={handleSelectAllInviteMembers}
              onConfirmInviteMembers={handleConfirmInviteMembers}
              onCloseInviteFromChallenge={handleCloseInviteFromChallenge}
              isLoadingInviteMembers={isLoadingInviteMembers}
              isAddingInviteMembers={isAddingInviteMembers}
            />
          )}
        </div>
        
        {/* Bottom navigation */}
        <div style={{
          display: 'flex',
          borderTop: '1px solid var(--md-sys-color-outline-variant)',
          backgroundColor: 'var(--md-sys-color-surface-container)',
          padding: '8px 0'
        }}>
          {[
            { id: 'reading', icon: 'readingTab', label: 'Reading' },
            {
              id: 'leaderboard',
              icon: 'leaderboardTab',
              label: challengeType === 'cooperative' ? 'Progress' : 'Leaderboard'
            },
            { id: 'info', icon: 'infoTab', label: 'Info' },
            { id: 'settings', icon: 'settingsTab', label: 'Challenge Settings' }
          ].map(tab => (
            <div
              key={tab.id}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '8px 4px',
                color: activeTab === tab.id ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface-variant)'
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon name={tab.icon} style={{ fontSize: '24px', marginBottom: '4px' }} />
              <span style={{ fontSize: '12px', fontWeight: activeTab === tab.id ? '500' : '400' }}>
                {tab.label}
              </span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'var(--md-sys-color-inverse-surface)',
          color: 'var(--md-sys-color-inverse-on-surface)',
          padding: '12px 24px',
          borderRadius: '24px',
          fontSize: '14px',
          fontWeight: '500',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          maxWidth: '90vw',
          textAlign: 'center',
          animation: showToast ? 'slideUp 0.3s ease-out' : 'slideDown 0.3s ease-in'
        }} 
        dangerouslySetInnerHTML={{ __html: toastMessage }}>
        </div>
      )}
    </div>
  );
};